/**
 * Atlas PM · 审批工作台 · 数据 hooks
 *
 * 接口（已 curl 验证）：
 *   GET  /api/approvals?todoOnly=true   待我审批
 *   GET  /api/approvals                 全部（本项目/数据范围内）
 *   GET  /api/approvals/:id/timeline    审批时间线
 *   POST /api/approvals/:id/approve     同意
 *   POST /api/approvals/:id/reject      退回
 *
 * ⚠️ 后端列表接口**没有 mine 过滤参数**，故「我发起的」在客户端按
 *    `startedBy === 当前用户` 过滤（一次取 pageSize≤200 的窗口）。
 *
 * 解锁申请（P6.19）走独立接口：
 *   GET  /api/freeze/unlock-requests            列表（服务端按权限过滤）
 *   POST /api/freeze/unlock-requests/:id/approve
 *   POST /api/freeze/unlock-requests/:id/reject
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { useAuthStore } from '../../stores/auth';
import type { Paginated } from '../../api/types';
import type { ApprovalInstanceInfo } from '@atlaspm/shared';

export interface ApprovalBucket {
  items: ApprovalInstanceInfo[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

const DONE_STATUSES = ['APPROVED', 'REJECTED', 'CANCELLED', 'EXPIRED'];

/**
 * ⭐ P6.52 在途审批状态。与全国态势「审批积压」KPI 的口径一致
 * （`useMapData.ts` 的 `buildNationalKpis` 同用这两个状态）。
 */
const PENDING_STATUSES = ['PENDING', 'APPROVING'];

/** 待我审批 */
export function useTodoApprovals(): ApprovalBucket {
  const q = useQuery({
    queryKey: qk.approvals({ todoOnly: true }),
    queryFn: () =>
      api.get<Paginated<ApprovalInstanceInfo>>(`/approvals${qs({ todoOnly: true, pageSize: 200 })}`),
  });
  return {
    items: q.data?.items ?? [],
    total: q.data?.total ?? 0,
    isLoading: q.isLoading,
    isError: q.isError,
    error: (q.error as Error | null) ?? null,
  };
}

/** 全部审批（用于「我发起的」与「已办结」的客户端分桶） */
export function useAllApprovals(): ApprovalBucket {
  const q = useQuery({
    queryKey: qk.approvals({ scope: 'all' }),
    queryFn: () =>
      api.get<Paginated<ApprovalInstanceInfo>>(`/approvals${qs({ pageSize: 200 })}`),
  });
  return {
    items: q.data?.items ?? [],
    total: q.data?.total ?? 0,
    isLoading: q.isLoading,
    isError: q.isError,
    error: (q.error as Error | null) ?? null,
  };
}

export interface Buckets {
  /**
   * ⭐ P6.52 全部待审批（PENDING / APPROVING，数据范围内）。
   * 与全国态势「审批积压」KPI 同口径，保证 KPI 数字与点进来的列表条数一致。
   */
  pending: ApprovalInstanceInfo[];
  /** 其中「待我审批」（审批人含当前用户且轮到其所在节点） */
  todo: ApprovalInstanceInfo[];
  mine: ApprovalInstanceInfo[];
  done: ApprovalInstanceInfo[];
  counts: { pending: number; todo: number; mine: number; done: number };
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export function useApprovalBuckets(): Buckets {
  const userId = useAuthStore((s) => s.user?.userId);
  const todo = useTodoApprovals();
  const all = useAllApprovals();

  const pending = all.items.filter((a) => PENDING_STATUSES.includes(a.status));
  const mine = userId ? all.items.filter((a) => a.startedBy === userId) : [];
  const done = all.items.filter((a) => DONE_STATUSES.includes(a.status));

  return {
    pending,
    todo: todo.items,
    mine,
    done,
    counts: {
      pending: pending.length,
      todo: todo.total,
      mine: mine.length,
      done: done.length,
    },
    isLoading: todo.isLoading || all.isLoading,
    isError: todo.isError || all.isError,
    error: todo.error ?? all.error,
  };
}

/** 同意 */
export function useApprove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      api.post(`/approvals/${id}/approve`, comment ? { comment } : {}),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['approvals'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/** 退回（理由必填，由弹窗保证） */
export function useReject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      api.post(`/approvals/${id}/reject`, { comment }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['approvals'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/**
 * ⭐ P6.16 终止「已超时(EXPIRED)」的审批（人工脱困，原因必填）。
 * 成功后任务回到 IN_PROGRESS，可修改后重新提交。
 */
export function useTerminateApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, reason }: { taskId: string; reason: string }) =>
      api.post(`/tasks/${taskId}/approval/terminate`, { reason }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['approvals'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/** P6.16 卡住的任务（任务 APPROVING 却无进行中的审批实例） */
export interface StuckTaskItem {
  taskId: string;
  taskCode: string;
  taskName: string;
  projectId: string;
  projectName: string | null;
  taskState: string;
  instanceId: string | null;
  instanceStatus: string | null;
  slaDeadline: string | null;
  submittedBy: string | null;
  reason: 'INSTANCE_EXPIRED' | 'NO_INSTANCE';
}

/** ⭐ P6.16 一致性检查：拉取卡住的审批任务（只读） */
export function useStuckTasks() {
  return useQuery({
    queryKey: ['approvals', 'stuck'] as const,
    queryFn: () => api.get<{ items: StuckTaskItem[]; total: number }>('/approvals/stuck'),
  });
}

/* ─────────────────────────────────────────
   解锁申请（P6.19：真实列表 + 批准 / 拒绝）
   ───────────────────────────────────────── */

/** 与后端 UnlockRequestListItem 对齐 */
export type UnlockRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'USED';

export interface UnlockRequestItem {
  id: string;
  taskId: string | null;
  taskCode: string | null;
  taskName: string | null;
  projectId: string | null;
  projectName: string | null;
  applicantId: string;
  applicantName: string | null;
  reason: string | null;
  status: UnlockRequestStatus;
  currentNodeSeq: number;
  currentNodeName: string | null;
  slaDeadline: string | null;
  slaRemainHours: number | null;
  createdAt: string;
  /** 服务端判定：超管或持 freeze:unlock_request */
  canIApprove: boolean;
}

/** 解锁申请列表（服务端已按权限过滤） */
export function useUnlockRequests(status?: UnlockRequestStatus) {
  return useQuery({
    queryKey: ['unlockRequests', status ?? 'all'] as const,
    queryFn: () =>
      api.get<{ items: UnlockRequestItem[]; total: number }>(
        `/freeze/unlock-requests${qs({ pageSize: 100, status })}`,
      ),
    select: (r) => r.items,
  });
}

/** 批准解冻（进入 72 小时编辑窗口） */
export function useApproveUnlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      api.post(
        `/freeze/unlock-requests/${id}/approve`,
        comment ? { comment } : {},
      ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['unlockRequests'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}

/** 拒绝解冻（理由必填且不少于 20 字，由后端与弹窗双重校验） */
export function useRejectUnlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      api.post(`/freeze/unlock-requests/${id}/reject`, { comment }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['unlockRequests'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}
